import { List, useTable } from "@refinedev/antd";
import { Badge, Segmented, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router";

import { DATE_TIME_FORMAT, SUPPORT_STATUS } from "../labels";
import type { SupportThreadItem } from "../types";

// Product decision 2026-10-13: a thread stays in "Cevap bekleyen" until someone answers or marks
// it solved, read or not, so nothing is missed.
const VIEW_OPTIONS = [
  { value: "awaiting", label: "Cevap bekleyen" },
  { value: "open", label: "Açık" },
  { value: "resolved", label: "Çözülen" },
  { value: "all", label: "Tümü" },
];

export const AUDIENCE_LABELS: Record<string, string> = { STUDENT: "Öğrenci", TUTOR: "Eğitmen" };

/**
 * /support: students' and tutors' messages to HedefUni, latest first (permission
 * `support.view`; answering needs `support.reply`).
 */
export function SupportPage() {
  const navigate = useNavigate();
  const { tableProps, filters, setFilters } = useTable<SupportThreadItem>({
    resource: "support",
    pagination: { mode: "server", pageSize: 25 },
    filters: { initial: [{ field: "view", operator: "eq", value: "awaiting" }] },
    syncWithLocation: true,
  });
  const view =
    (filters.find((item) => "field" in item && item.field === "view")?.value as string) ??
    "awaiting";

  return (
    <List title="Destek">
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={VIEW_OPTIONS}
          value={view}
          onChange={(value) =>
            setFilters([{ field: "view", operator: "eq", value: String(value) }], "replace")
          }
        />
      </Space>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: "max-content" }}
        // The whole row opens it, not only the name.
        onRow={(record) => ({
          onClick: () => navigate(`/support/${record.id}`),
          style: { cursor: "pointer" },
        })}
      >
        <Table.Column<SupportThreadItem>
          title="Kişi"
          dataIndex="person"
          render={(person: SupportThreadItem["person"], thread) => (
            <Space direction="vertical" size={0}>
              <Link to={`/support/${thread.id}`}>
                <Typography.Text strong>{person.name}</Typography.Text>
              </Link>
              <Typography.Text type="secondary">{person.email}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<SupportThreadItem>
          title="Panel"
          dataIndex={["person", "audience"]}
          render={(audience: string) => <Tag>{AUDIENCE_LABELS[audience]}</Tag>}
        />
        <Table.Column<SupportThreadItem>
          title="Son mesaj"
          dataIndex="lastMessage"
          render={(last: SupportThreadItem["lastMessage"]) =>
            last ? (
              <Typography.Text ellipsis style={{ maxWidth: 360 }}>
                {last.fromUser ? "" : "Ekip: "}
                {last.body}
              </Typography.Text>
            ) : (
              "—"
            )
          }
        />
        <Table.Column<SupportThreadItem>
          title="Zaman"
          dataIndex="lastMessage"
          key="time"
          render={(last: SupportThreadItem["lastMessage"]) =>
            last ? dayjs(last.createdAt).format(DATE_TIME_FORMAT) : "—"
          }
        />
        <Table.Column<SupportThreadItem>
          title="Durum"
          key="status"
          render={(_, thread) => (
            <Space direction="vertical" size={0}>
              <Space size={6}>
                <Tag color={SUPPORT_STATUS[thread.status]?.color}>
                  {SUPPORT_STATUS[thread.status]?.label}
                </Tag>
                {thread.unreadCount > 0 ? <Badge count={thread.unreadCount} /> : null}
              </Space>
              {thread.status === "AWAITING" && thread.lastMessage ? (
                <Typography.Text type="secondary">
                  {dayjs(thread.lastMessage.createdAt).fromNow(true)} bekliyor
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
      </Table>
    </List>
  );
}
